import React from "react";
import { SectionPage } from "./sectionPage";
import { SectionForm } from "./sectionForm";
import { SectionView } from "./sectionView";
import { GRID_ACTION_TYPES, GridActionType } from "../../../assets/types";
import { isGridActionType } from "../../../assets/utils/helpers";
import { SectionService } from "./sectionService";
import { Constants } from "../../../assets/utils/lib/constants";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { SectionDto } from "../../../services/dto";
import { useQueryClient } from "@tanstack/react-query";
import { apiErrorMessage } from "../../../assets/utils/lib/error";

interface QuestionnaireSectionContainerProps {
  questionnaireId?: string | undefined;
  questionnaireType?: string | undefined;
}

export const SectionContainer = (props: QuestionnaireSectionContainerProps) => {
  const queryClient = useQueryClient();
  const [data, setData] = React.useState<SectionDto>();
  const [drawerType, setDrawerType] = React.useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );
  const { showSnackbar } = useSnackbar();

  const sectionQuery = SectionService.getSectionQuery(
    data?.id ?? Constants.emptyGuid
  );

  const { mutateAsync: saveQuestionnaireSection } = SectionService.save();
  const { mutate: deleteQuestionnaireSection } = SectionService.deleteSection();

  const isEditMode = !!(data?.id && data.id !== Constants.emptyGuid);

  const refreshGrid = (): void => {
    isEditMode && sectionQuery.refetch();
    queryClient.invalidateQueries({
      queryKey: ["questionnaireSectionGridQuery"],
    });
    closeDrawer();
  };

  const handleApiResponse = (response: any, successMessage: string): void => {
    const errorMsg = apiErrorMessage(response.errorMessages);
    if (errorMsg) {
      showSnackbar(errorMsg, MessageSeverity.Error);
    } else {
      showSnackbar(successMessage, MessageSeverity.Success);
      refreshGrid();
    }
  };

  const handleSubmit = async (values: SectionDto) => {
    values.id = isEditMode ? data!.id : Constants.emptyGuid;
    const message = isEditMode ? "Section updated" : "Section saved";
    return await saveQuestionnaireSection(values, {
      onSuccess: (response) => handleApiResponse(response, message),
    });
  };

  const handleDelete = (): void => {
    deleteQuestionnaireSection(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response) => handleApiResponse(response, "Section deleted"),
    });
  };

  const handleCustomEvent = (actionType: string, dataId: string): void => {
    if (isGridActionType(actionType)) {
      setDrawerType(actionType);
      setData({ id: dataId } as SectionDto);      
    }
  };

  const closeDrawer = (): void => setDrawerType(GRID_ACTION_TYPES.HIDDEN);

  const handleEdit = (): void => {
    setData(sectionQuery.data);
    setDrawerType(GRID_ACTION_TYPES.EDIT);
  };

  const renderDrawerContent = (): React.JSX.Element | null => {
    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW:
        return (
          <SectionForm
            show={true}
            onHide={closeDrawer}
            onSave={handleSubmit}
            questionnaireId={props.questionnaireId}
          />
        );

      case GRID_ACTION_TYPES.EDIT:
        return (
          <SectionForm
            data={sectionQuery?.data}
            show={true}
            loading={sectionQuery.isLoading}
            onHide={closeDrawer}
            onSave={handleSubmit}
          />
        );

      case GRID_ACTION_TYPES.VIEW:
      case GRID_ACTION_TYPES.DOUBLE_CLICK:
        return (
          <SectionView
            data={sectionQuery?.data}
            show={true}
            onHide={closeDrawer}
            onDelete={handleDelete}
            onEdit={handleEdit}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SectionPage
        onCustomEvent={handleCustomEvent}
        questionnaireId={props.questionnaireId}
        questionnaireType={props.questionnaireType}
      />

      {renderDrawerContent()}
    </>
  );
};
